import React, { useState, useEffect, useRef, Suspense } from "react";
import { useTranslation } from "react-i18next";
import "./App.css";
import axios from "axios";
import { BrowserRouter, Routes, Route, Navigate, useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { Button } from "./components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Badge } from "./components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Separator } from "./components/ui/separator";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./components/ui/dialog";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Alert, AlertDescription } from "./components/ui/alert";
import { BookOpen, FileText, Download, Shuffle, Loader2, GraduationCap, AlertCircle, CheckCircle, Crown, CreditCard, LogIn, LogOut, Mail, RefreshCw, Lock, KeyRound } from "lucide-react";
import { useToast } from "./hooks/use-toast";
import TemplateSettings from "./components/TemplateSettings";
// S2-7: Lazy-loaded sheet/exercise pages
// P0.5: SheetBuilderPageV2 n'est plus routé (/builder-v2 redirige vers /generer) — suppression du fichier prévue en P2.1
const MySheetsPageP31 = React.lazy(() => import("./components/MySheetsPageP31"));
const SheetEditPageP31 = React.lazy(() => import("./components/SheetEditPageP31"));
// P1.4: MyExercisesPage est rendue par MySheetsPageP31 (onglet « Exercices enregistrés »)
// P1.5: ProSettingsPage est désormais rendue en onglet par AccountPage (mode embedded)
// Refonte « Encre & Papier » : l'atelier unique remplace le couple
// générateur (ExerciseGeneratorPage) + composer (SheetComposerPage).
import AtelierPage from "./components/AtelierPage";
// Architecture H — la fiche composée. Vit À CÔTÉ de l'atelier, qui reste
// le parcours servi par défaut tant que B1 n'est pas validé.
import FichePageH from "./components/fiche/FichePageH";
import AccueilFiche from "./components/fiche/AccueilFiche";
import AdminGuard from "./components/AdminGuard"; // P0.3: garde des routes /admin/*
import AdminLayout from "./components/admin/AdminLayout"; // P2.3a: layout admin à sidebar
const AccountPage = React.lazy(() => import("./components/AccountPage")); // P0.6: Mon compte
// S2-7: Lazy-loaded admin pages
import ErrorBoundary from "./components/ErrorBoundary";

const QualityCockpitPage = React.lazy(() => import("./components/admin/QualityCockpitPage"));
const AujourdhuiPage = React.lazy(() => import("./components/admin/dossier/AujourdhuiPage"));
const DossierPage = React.lazy(() => import("./components/admin/dossier/DossierPage"));
const MiseEnLignePage = React.lazy(() => import("./components/admin/dossier/MiseEnLignePage"));
const AdminPrototypePage = React.lazy(() => import("./components/admin/AdminPrototypePage"));
const ATraiterPage = React.lazy(() => import("./components/admin/ATraiterPage"));
const ProgrammePage = React.lazy(() => import("./components/admin/ProgrammePage"));
const PretsNonEnLignePage = React.lazy(() => import("./components/admin/PretsNonEnLignePage"));
// CurriculumAdminSimplePage : importé mais JAMAIS routé (vérifié). Import retiré
// du bundle ; le fichier est conservé le temps de confirmer qu'il ne sert plus.
const CurriculumAdminPage = React.lazy(() => import("./components/admin/CurriculumAdminPage"));
const ChapterExercisesAdminPage = React.lazy(() => import("./components/admin/ChapterExercisesAdminPage"));
const GeneratorTemplatesAdminPage = React.lazy(() => import("./components/admin/GeneratorTemplatesAdminPage"));
const ArchivedExercisesAdminPage = React.lazy(() => import("./components/admin/ArchivedExercisesAdminPage"));
const ReferentielAdminPage = React.lazy(() => import("./components/admin/ReferentielAdminPage"));
const RetoursPage = React.lazy(() => import("./components/admin/RetoursPage"));
const AtelierGenerateursPage = React.lazy(() => import("./components/admin/AtelierGenerateursPage"));
import NavBar from "./components/NavBar";
import SelectionFloatingButton from "./components/SelectionFloatingButton"; // P1.1: « Ma fiche en cours (N) »
import Footer from "./components/Footer";
// S2-7: Lazy-loaded pages
const CheckoutPage = React.lazy(() => import("./components/CheckoutPage"));
const ResetPasswordPage = React.lazy(() => import("./components/ResetPasswordPage"));
const PricingPage = React.lazy(() => import("./components/PricingPage"));
const LegalPages = React.lazy(() => import("./components/LegalPages"));
const BetaFondateurPage = React.lazy(() => import("./components/BetaFondateurPage"));
// We need named exports from LegalPages, so we wrap them
const MentionsLegales = React.lazy(() => import("./components/LegalPages").then(m => ({ default: m.MentionsLegales })));
const CGU = React.lazy(() => import("./components/LegalPages").then(m => ({ default: m.CGU })));
const CGV = React.lazy(() => import("./components/LegalPages").then(m => ({ default: m.CGV })));
const ConditionsBeta = React.lazy(() => import("./components/LegalPages").then(m => ({ default: m.ConditionsBeta })));
const PolitiqueConfidentialite = React.lazy(() => import("./components/LegalPages").then(m => ({ default: m.PolitiqueConfidentialite })));
const Confiance = React.lazy(() => import("./components/LegalPages").then(m => ({ default: m.Confiance })));
import CookieBanner from "./components/CookieBanner";
import UpgradeProModal, { trackPremiumEvent } from "./components/UpgradeProModal";
import { useBeta } from "./hooks/useBeta";
import { Toaster } from "./components/ui/toaster";
import { LoginProvider } from "./contexts/LoginContext";
import { SelectionProvider } from "./contexts/SelectionContext";
import GlobalLoginModal from "./components/GlobalLoginModal";
const AboutPage = React.lazy(() => import("./components/AboutPage"));
// Prototype de refonte visuelle — jamais monté en production (le garde
// NODE_ENV est résolu au build : la route ET le chunk sont éliminés).
const DesignPreview = process.env.NODE_ENV !== "production"
  ? React.lazy(() => import("./design-preview/DesignPreview"))
  : null;
import { useAuth } from "./hooks/useAuth";
import { useExportPdfGate } from "./lib/exportPdfUtils";
import { useLogin } from "./contexts/LoginContext";
import { getApiErrorMessage } from "./lib/errorMessage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// P3.1: Redirection vers nouveau système "Mes fiches"
function RedirectToNewSheets() {
  const navigate = useNavigate();
  
  useEffect(() => {
    navigate('/mes-fiches', { replace: true });
  }, [navigate]);
  
  return (
    <div className="flex items-center justify-center min-h-screen">
      <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
    </div>
  );
}

// Configure axios global timeout (15 seconds)
axios.defaults.timeout = 15000;

// Payment Success Component
function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      setError("Session ID manquant");
      setCheckingStatus(false);
      return;
    }

    pollPaymentStatus(sessionId);
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000; // 2 seconds

    if (attempts >= maxAttempts) {
      setError('Vérification du paiement expirée. Veuillez contacter le support.');
      setCheckingStatus(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      
      // P0.2: "no_payment_required" = essai gratuit 7 jours (aucun débit immédiat)
      if (response.data.payment_status === 'paid' || response.data.payment_status === 'no_payment_required') {
        setPaymentStatus('success');
        setCheckingStatus(false);
        
        // Redirect to main app after 3 seconds
        setTimeout(() => {
          navigate('/');
        }, 3000);
        return;
      } else if (response.data.status === 'expired') {
        setError('Session de paiement expirée. Veuillez réessayer.');
        setCheckingStatus(false);
        return;
      }

      // Continue polling if payment is still pending
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setError('Erreur lors de la vérification du paiement.');
      setCheckingStatus(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <GraduationCap className="mx-auto h-12 w-12 text-blue-600 mb-4" />
          <CardTitle>Le Maître Mot - Paiement</CardTitle>
        </CardHeader>
        <CardContent className="text-center">
          {checkingStatus ? (
            <div>
              <Loader2 className="mx-auto h-8 w-8 animate-spin text-blue-600 mb-4" />
              <p>Vérification du paiement en cours...</p>
            </div>
          ) : error ? (
            <div>
              <AlertCircle className="mx-auto h-8 w-8 text-red-600 mb-4" />
              <p className="text-red-600 mb-4">{error}</p>
              <Button onClick={() => navigate('/')} variant="outline">
                Retour à l'accueil
              </Button>
            </div>
          ) : paymentStatus === 'success' ? (
            <div>
              <CheckCircle className="mx-auto h-8 w-8 text-green-600 mb-4" />
              <h3 className="text-lg font-semibold mb-2">Paiement réussi !</h3>
              <p className="text-gray-600 mb-2">Votre abonnement Pro est maintenant actif</p>
              <p className="text-sm text-gray-500 mb-4">
                Vous avez accès aux exports illimités
              </p>
              <p className="text-sm text-gray-500">
                Redirection automatique vers l'accueil...
              </p>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}

// Payment Cancel Component
function PaymentCancel() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <GraduationCap className="mx-auto h-12 w-12 text-blue-600 mb-4" />
          <CardTitle>Le Maître Mot - Paiement Annulé</CardTitle>
        </CardHeader>
        <CardContent className="text-center">
          <AlertCircle className="mx-auto h-8 w-8 text-orange-600 mb-4" />
          <h3 className="text-lg font-semibold mb-2">Paiement annulé</h3>
          <p className="text-gray-600 mb-4">
            Votre paiement a été annulé. Vous pouvez réessayer à tout moment.
          </p>
          <Button onClick={() => navigate('/')} className="w-full">
            Retour à l'accueil
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

// Login Verification Component (for magic link)
function LoginVerify() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  // P0-A1: useRef guard prevents multiple calls (React StrictMode safe)
  const didCallRef = useRef(false);

  useEffect(() => {
    // P0-A1: Guard with useRef - prevents duplicate calls in StrictMode
    if (didCallRef.current) {
      console.log('[LoginVerify] Already called, skipping (StrictMode guard)');
      return;
    }

    const token = searchParams.get('token');
    if (!token) {
      setError("Token manquant");
      setVerifying(false);
      return;
    }

    // Mark as called BEFORE async call to prevent race conditions
    didCallRef.current = true;
    verifyLogin(token);
  }, [searchParams]); // P0-A1: Removed isVerifying from deps

  const verifyLogin = async (token) => {
    
    try {
      // Generate device ID
      const deviceId = localStorage.getItem('lemaitremot_device_id') || generateDeviceId();
      localStorage.setItem('lemaitremot_device_id', deviceId);

      console.log('🔐 Verifying login token...');
      const response = await axios.post(`${API}/auth/verify-login`, {
        token: token,
        device_id: deviceId
      }, {
        withCredentials: true // P0 UX: Inclure les cookies httpOnly
      });

      // Store session token and user info
      localStorage.setItem('lemaitremot_session_token', response.data.session_token);
      localStorage.setItem('lemaitremot_user_email', response.data.email);
      localStorage.setItem('lemaitremot_login_method', 'session');

      console.log('✅ Login verification successful');
      setSuccess(true);
      
      // P0 UX: Rediriger vers returnTo si présent
      const returnTo = sessionStorage.getItem('postLoginRedirect') || '/';
      sessionStorage.removeItem('postLoginRedirect'); // Nettoyer après utilisation
      
      setTimeout(() => {
        navigate(returnTo);
      }, 2000);

    } catch (error) {
      console.error('Error verifying login:', error);
      // `detail` peut être un objet ({error, message}) : ne jamais le mettre
      // brut dans un state rendu par React (écran blanc garanti).
      setError(getApiErrorMessage(error, 'Erreur lors de la vérification'));
    } finally {
      setVerifying(false);
    }
  };

  const generateDeviceId = () => {
    return 'device_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center mb-4">
            <GraduationCap className="h-8 w-8 text-blue-600 mr-2" />
            <h1 className="text-2xl font-bold text-gray-900">Le Maître Mot</h1>
          </div>
          <CardTitle>Connexion en cours</CardTitle>
        </CardHeader>
        <CardContent className="text-center">
          {verifying ? (
            <div className="space-y-4">
              <Loader2 className="h-8 w-8 text-blue-600 animate-spin mx-auto" />
              <p className="text-gray-600">Vérification de votre connexion...</p>
            </div>
          ) : error ? (
            <div className="space-y-4">
              <AlertCircle className="h-8 w-8 text-red-600 mx-auto" />
              <p className="text-red-600">{error}</p>
              <Button 
                onClick={() => navigate('/')}
                variant="outline"
                className="w-full"
              >
                Retour à l'accueil
              </Button>
            </div>
          ) : success ? (
            <div className="space-y-4">
              <CheckCircle className="h-8 w-8 text-green-600 mx-auto" />
              <p className="text-green-600">✅ Connexion réussie !</p>
              <p className="text-sm text-gray-500">Redirection en cours...</p>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}


// Component wrapper avec NavBar
// P2.3a: enveloppe commune des pages admin — garde + nav + layout à sidebar
function AdminPage({ children }) {
  return (
    <AdminGuard>
      <AppWithNav>
        <AdminLayout>{children}</AdminLayout>
      </AppWithNav>
    </AdminGuard>
  );
}

/**
 * Pendant la bêta fondateur, la grille tarifaire cède la place à la page Bêta.
 *
 * L'aiguillage est ICI, au niveau de la route, et non dans `PricingPage` :
 * un retour anticipé à l'intérieur de ce composant s'exécuterait AVANT ses
 * autres hooks et violerait les règles de React. Ici, l'un ou l'autre est
 * monté — jamais un composant à moitié rendu.
 *
 * La page tarifaire reste intacte et reprend sa place le jour où
 * `BETA_FONDATEUR_JOURS=0`, sans rien à reconstruire.
 */
function PaiementOuBeta() {
  const beta = useBeta();
  if (beta.chargement) return null;
  return beta.active ? <BetaFondateurPage /> : <CheckoutPage />;
}

/**
 * `/cgv` pendant la bêta : des conditions de bêta, pas des conditions de vente.
 *
 * Les CGV annonçaient 9,99 €/mois, 99 €/an et un essai de 7 jours, pour une
 * offre dont toutes les routes répondent 403. Elles restent dans le fichier
 * comme archive technique, mais ne sont plus présentées comme les conditions
 * commerciales en vigueur — il n'y en a aucune.
 */
function ConditionsOuCGV() {
  const beta = useBeta();
  if (beta.chargement) return null;
  return beta.active ? <ConditionsBeta jours={beta.jours} /> : <CGV />;
}

function TarifsOuBeta() {
  const beta = useBeta();
  if (beta.chargement) return null;
  return beta.active ? <BetaFondateurPage /> : <PricingPage />;
}

function AppWithNav({ children, showNav = true }) {
  if (!showNav) {
    return <>{children}</>;
  }
  return (
    <div className="w-full rogne-x">
      <NavBar />
      {children}
      {/* P1.1: remplace l'entrée de nav « Composer (N) » */}
      <SelectionFloatingButton />
      <Footer />
    </div>
  );
}

// P0: Guard component pour protéger les routes Pro

// P0: Guard component pour protéger les routes qui nécessitent une authentification
function AuthRequiredGuard({ children }) {
  const { sessionToken, isLoading } = useAuth();
  const { openLogin } = useLogin();
  const location = useLocation();
  const navigate = useNavigate();

  // L'ADRESSE COMPLÈTE, PAS SEULEMENT LE CHEMIN.
  //
  // « /mes-fiches?tab=exercices » et « /mes-fiches » ne mènent pas au même
  // endroit : le premier ouvre les exercices enregistrés, le second les
  // fiches. Ne garder que `pathname` renvoyait TOUJOURS sur les fiches après
  // connexion — donc « Voir mes exercices » ne montrait jamais les exercices.
  const destination = `${location.pathname}${location.search || ""}`;

  // Les effets de bord — ouvrir un modal, naviguer — n'ont rien à faire dans
  // un rendu. Appelés là, ils modifiaient l'état d'un autre composant pendant
  // le rendu de celui-ci, et le renvoi vers /generer partait même quand la
  // session arrivait juste après.
  React.useEffect(() => {
    if (isLoading || sessionToken) return undefined;
    openLogin(destination);
    const minuteur = setTimeout(() => navigate('/generer'), 100);
    return () => clearTimeout(minuteur);
  }, [isLoading, sessionToken, destination, openLogin, navigate]);

  // Tant qu'on ne sait pas, on n'affiche ni la page ni une redirection.
  if (isLoading || !sessionToken) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Si connecté, afficher la page normale
  return <>{children}</>;
}

/**
 * Remet le défilement en haut à chaque changement de PAGE.
 *
 * POURQUOI. En navigation client, le navigateur ne restaure rien : on arrivait
 * sur la page suivante à la hauteur qu'on occupait sur la précédente. Mesuré
 * avant correctif : partir de Tarifs à y = 923 et cliquer « À propos » laissait
 * la nouvelle page à y = 1942 — sous son propre contenu.
 *
 * On n'écoute QUE `pathname` : `search` change quand on bascule d'onglet à
 * l'intérieur d'une même page (« Mes fiches ?tab=… »), et remonter là serait
 * une gêne, pas un service. `behavior: "auto"` parce qu'un défilement animé sur
 * un changement de page donne le mal de mer, et il est ignoré si l'utilisateur
 * a demandé moins d'animations.
 */
function RemonterEnHaut() {
  const { pathname } = useLocation();

  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname]);

  return null;
}

// Redirect component pour normaliser les routes
function RedirectToGenerer() {
  const navigate = useNavigate();
  const location = useLocation();

  React.useEffect(() => {
    // Normaliser les variations de casse
    const normalizedPath = location.pathname.toLowerCase();
    if (normalizedPath === '/générer' || normalizedPath === '/generer' || normalizedPath === '/Générer') {
      navigate('/generer', { replace: true });
      return;
    }
    
    // Rediriger toute route inconnue vers /generer
    navigate('/generer', { replace: true });
  }, [location.pathname, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center">
      <div className="text-center">
        <p className="text-gray-600 mb-4">Redirection en cours...</p>
      </div>
    </div>
  );
}

// 404 Component
function NotFoundPage() {
  const navigate = useNavigate();

  // Le serveur sert index.html pour toute URL — c'est ce qui fait marcher le
  // routage client — donc le statut HTTP reste 200 et ne peut pas dire 404 sans
  // casser la navigation. On le dit donc aux robots, faute de pouvoir le dire
  // au protocole.
  React.useEffect(() => {
    const balise = document.createElement("meta");
    balise.name = "robots";
    balise.content = "noindex";
    document.head.appendChild(balise);
    return () => { document.head.removeChild(balise); };
  }, []);

  return (
    <div className="flex min-h-[70vh] items-center justify-center bg-background px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="font-serif text-2xl">Page introuvable</CardTitle>
          <CardDescription>
            Cette adresse ne correspond à aucune page du site.
          </CardDescription>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          <Button
            onClick={() => navigate('/generer')}
            className="w-full"
          >
            Aller au générateur
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate('/')}
            className="w-full"
          >
            Retour à l'accueil
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
    <BrowserRouter>
      <SelectionProvider>
      <LoginProvider>
        <RemonterEnHaut />
        <Toaster />
        <GlobalUpgradeModal />
        <GlobalLoginModal />
        <CookieBanner />
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>}>
        <Routes>
        {/* Routes spéciales sans NavBar */}
        {DesignPreview && <Route path="/design-preview" element={<DesignPreview />} />}
        <Route path="/success" element={<PaymentSuccess />} />
        <Route path="/cancel" element={<PaymentCancel />} />
        <Route path="/login/verify" element={<LoginVerify />} />
        {/* Pendant la bêta, la page de paiement cède la place — comme /pricing,
              et pour la même raison : ne jamais renvoyer quelqu'un en silence. */}
        <Route path="/checkout" element={<PaiementOuBeta />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} /> {/* P2: Reset password page */}
        {/* Routes principales avec NavBar */}
        <Route path="/pricing" element={
          <AppWithNav>
            <TarifsOuBeta />
          </AppWithNav>
        } /> {/* P2.3: Pricing page - avec NavBar pour navigation retour */}

        <Route path="/mentions-legales" element={
          <AppWithNav>
            <MentionsLegales />
          </AppWithNav>
        } />
        <Route path="/cgu" element={
          <AppWithNav>
            <CGU />
          </AppWithNav>
        } />
        <Route path="/cgv" element={
          <AppWithNav>
            <ConditionsOuCGV />
          </AppWithNav>
        } />
        <Route path="/politique-confidentialite" element={
          <AppWithNav>
            <PolitiqueConfidentialite />
          </AppWithNav>
        } />
        <Route path="/confiance" element={
          <AppWithNav>
            <Confiance />
          </AppWithNav>
        } />

        <Route path="/" element={
          <AppWithNav>
            <AccueilFiche />
          </AppWithNav>
        } />
        
        <Route path="/generer" element={
          <AppWithNav>
            <AtelierPage />
          </AppWithNav>
        } />

        {/* ARCHITECTURE H — parcours « classe -> objectif -> fiche ».
            La route existe toujours : c'est ce qui permet de COMPARER les deux
            parcours sur le même contenu, en production, sans redéploiement.
            Ce que le drapeau `REACT_APP_FICHE_H` commande, c'est le fait d'y
            CONDUIRE depuis l'accueil ; le retour en arrière est donc immédiat
            et ne casse aucun lien déjà partagé. */}
        <Route path="/fiche" element={
          <AppWithNav>
            <FichePageH />
          </AppWithNav>
        } />
        
        {/* Normalisation des variations de casse */}
        <Route path="/générer" element={<RedirectToGenerer />} />
        <Route path="/Générer" element={<RedirectToGenerer />} />
        
        {/* Route legacy /generate redirige vers /generer */}
        <Route path="/generate" element={<RedirectToGenerer />} />
        
        {/* Routes existantes avec NavBar */}
        {/* P3.1: Redirection ancien builder vers nouveau système */}
        <Route path="/builder" element={
          <AppWithNav>
            <RedirectToNewSheets />
          </AppWithNav>
        } />
        <Route path="/builder/:sheetId" element={
          <AppWithNav>
            <RedirectToNewSheets />
          </AppWithNav>
        } />
        {/* P0.5: un seul chemin de création — l'ancien builder "3 clics" redirige vers /generer */}
        <Route path="/builder-v2" element={<Navigate to="/generer" replace />} />
        {/* Legacy route - redirige vers nouveau */}
        <Route path="/sheets" element={
          <AppWithNav>
            <RedirectToNewSheets />
          </AppWithNav>
        } />
        {/* P3.1: Nouveau système "Mes fiches" - P1.2: Accessible aux Free users (authentification requise) */}
        <Route path="/mes-fiches" element={
          <AppWithNav>
            <AuthRequiredGuard>
              <MySheetsPageP31 />
            </AuthRequiredGuard>
          </AppWithNav>
        } />
        <Route path="/mes-fiches/:sheet_uid" element={
          <AppWithNav>
            <AuthRequiredGuard>
              <SheetEditPageP31 />
            </AuthRequiredGuard>
          </AppWithNav>
        } />
        {/* P1.4: bibliothèque unique — les exercices enregistrés sont un onglet de Mes fiches */}
        <Route path="/mes-exercices" element={<Navigate to="/mes-fiches?tab=exercices" replace />} />
        {/* P1.5: les réglages Pro vivent dans Mon compte > Personnalisation */}
        <Route path="/pro/settings" element={<Navigate to="/compte?tab=personnalisation" replace />} />
        {/* P0.6: Mon compte — infos + suppression de compte (RGPD), pour Free et Pro */}
        <Route path="/compte" element={
          <AppWithNav>
            <AuthRequiredGuard>
              <AccountPage />
            </AuthRequiredGuard>
          </AppWithNav>
        } />
        
        {/* Routes admin - ordre: plus spécifique d'abord */}
        {/* P0.3: protégées par AdminGuard · P2.3a: layout admin dédié (sidebar) */}

        {/* « À traiter » : ce qui demande une décision maintenant. C'est la
            page d'accueil de l'administration — on entre par le travail, pas
            par l'état du système. */}
        {/* Les TROIS écrans du parcours courant. On entre par « Aujourd'hui » :
            la première question d'une administratrice n'est pas « comment va le
            système ? » mais « qu'est-ce qu'on attend de moi ? ». */}
        {/* Prototype isolé : même garde admin, navigation et données propres. */}
        <Route path="/admin/prototype" element={
          <AdminGuard><AdminPrototypePage /></AdminGuard>
        } />
        <Route path="/admin/aujourdhui" element={
          <AdminPage><AujourdhuiPage /></AdminPage>
        } />
        <Route path="/admin/dossier/:identifiant/mise-en-ligne" element={
          <AdminPage><MiseEnLignePage /></AdminPage>
        } />
        <Route path="/admin/dossier/:identifiant" element={
          <AdminPage><DossierPage /></AdminPage>
        } />

        {/* « À traiter » reste servi : il garde ce que le dossier ne couvre pas
            — les décisions hors parcours, les contenus prêts que personne ne
            sert. Il passe sous « Avancé », son URL ne change pas. */}
        <Route path="/admin/a-traiter" element={
          <AdminPage><ATraiterPage /></AdminPage>
        } />
        <Route path="/admin" element={<Navigate to="/admin/aujourdhui" replace />} />

        {/* « Programme » : le Bulletin officiel, et ce qu'on en couvre. */}
        <Route path="/admin/programme" element={
          <AdminPage><ProgrammePage /></AdminPage>
        } />

        {/* Certifier n'est pas mettre en ligne : la liste de ce qui est prêt
            et que personne ne sert. Atteinte depuis « À traiter ». */}
        <Route path="/admin/prets-non-en-ligne" element={
          <AdminPage><PretsNonEnLignePage /></AdminPage>
        } />

        {/* Pilotage : conservé sous « Avancé ». Il garde l'inventaire des
            générateurs, l'historique des décisions et la file de
            qualification, que « Programme » et « Chapitres » n'ont pas encore
            absorbés. Rien n'est supprimé tant que rien ne les remplace. */}
        <Route path="/admin/pilotage" element={
          <AdminPage><QualityCockpitPage /></AdminPage>
        } />

        {/* « Chapitres » : le catalogue tel que l'utilisateur le voit.
            `/admin/curriculum` reste servi et redirige — le mot est un mot
            d'ingénieur, mais un lien noté quelque part ne doit pas se casser. */}
        <Route path="/admin/chapitres/:chapterCode/exercises" element={
          <AdminPage><ChapterExercisesAdminPage /></AdminPage>
        } />
        <Route path="/admin/chapitres/:niveau" element={
          <AdminPage><CurriculumAdminPage /></AdminPage>
        } />
        <Route path="/admin/chapitres" element={
          <AdminPage><CurriculumAdminPage /></AdminPage>
        } />
        <Route path="/admin/curriculum/:chapterCode/exercises" element={
          <AdminPage><ChapterExercisesAdminPage /></AdminPage>
        } />
        <Route path="/admin/curriculum/:niveau" element={
          <AdminPage><CurriculumAdminPage /></AdminPage>
        } />
        <Route path="/admin/curriculum" element={
          <Navigate to="/admin/chapitres" replace />
        } />
        <Route path="/admin/templates" element={
          <AdminPage><GeneratorTemplatesAdminPage /></AdminPage>
        } />
        <Route path="/admin/exercises-archive" element={
          <AdminPage><ArchivedExercisesAdminPage /></AdminPage>
        } />
        <Route path="/admin/referentiel" element={
          <AdminPage><ReferentielAdminPage /></AdminPage>
        } />
        {/* « Retours » : la boucle avec les utilisateurs. L'ancienne URL
            reste servie et redirige. */}
        <Route path="/admin/retours" element={
          <AdminPage><RetoursPage /></AdminPage>
        } />
        {/* L'écran précédent a été retiré, pas seulement caché.
            Il avait été gardé le temps de vérifier que « Retours » ne faisait
            rien perdre : ses filtres par nature et par priorité y sont
            désormais. Le garder plus longtemps aurait été pire que l'enlever,
            car il MENTAIT : ses quatre libellés d'état étaient codés en dur et
            ne connaissaient ni « Non reproductible » ni « Vérifié ». Un ticket
            dans l'un de ces états s'y affichait sans nom. */}
        <Route path="/admin/bug-reports" element={
          <Navigate to="/admin/retours" replace />
        } />
        {/* Atelier : la CRÉATION d'un générateur, du besoin au site. Page
            dédiée et non un onglet du pilotage : le pilotage supervise un parc
            existant, l'atelier suit un parcours linéaire sur un seul objet.
            Réservée au poste de développement côté backend. */}
        <Route path="/admin/atelier" element={
          <AdminPage><AtelierGenerateursPage /></AdminPage>
        } />

        {/* P0.5: un seul chemin de création — l'ancien wizard MainApp redirige vers /generer */}
        <Route path="/legacy" element={<Navigate to="/generer" replace />} />
        
        {/* S2-5: Page A propos */}
        <Route path="/a-propos" element={
          <AppWithNav>
            <AboutPage />
          </AppWithNav>
        } />

        {/* Refonte : la composition vit dans l'atelier — redirection des favoris */}
        <Route path="/fiches/nouvelle" element={<Navigate to="/generer" replace />} />

        {/* Catch-all: rediriger vers /generer */}
        {/* Une adresse inconnue affichait le générateur, avec un statut 200 et
            sans un mot : l'utilisateur croyait avoir cliqué de travers. Elle
            affiche désormais une vraie page « introuvable », avec la barre de
            navigation pour repartir. Les variations de casse de /generer
            restent traitées par les routes explicites ci-dessus. */}
        <Route path="*" element={<AppWithNav><NotFoundPage /></AppWithNav>} />
      </Routes>
      </Suspense>
      </LoginProvider>
      </SelectionProvider>
    </BrowserRouter>
    </ErrorBoundary>
  );
}

// P2.3: Global Upgrade Pro Modal (outside router for global access)
function GlobalUpgradeModal() {
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeContext, setUpgradeContext] = useState('general');

  // Listen for custom events to open modal
  useEffect(() => {
    const handleOpenModal = (event) => {
      setUpgradeContext(event.detail?.context || 'general');
      setShowUpgradeModal(true);
    };
    
    window.addEventListener('openUpgradeModal', handleOpenModal);
    return () => window.removeEventListener('openUpgradeModal', handleOpenModal);
  }, []);

  return (
    <UpgradeProModal
      isOpen={showUpgradeModal}
      onClose={() => setShowUpgradeModal(false)}
      context={upgradeContext}
    />
  );
}

export default App;
